import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Home, FileText, CreditCard, Calendar, AlertTriangle } from 'lucide-react';

const CGV = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Conditions Générales de Vente - Frelon33™ | Services Anti-Nuisibles";
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-orange-50/30">
      {/* Hero Section CGV */}
      <section className="bg-gradient-to-r from-orange-600 to-red-500 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center frelon-slide-up">
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              Conditions Générales de Vente
            </h1>
            <p className="text-xl font-medium opacity-90">
              Conditions commerciales - <span className="frelon33-brand">Frelon33™</span>
            </p>
            <div className="mt-8">
              <Link
                to="/"
                className="frelon-btn frelon-btn-secondary inline-flex items-center space-x-2 px-6 py-3 bg-white text-orange-600 hover:bg-orange-50"
              >
                <Home className="h-5 w-5" />
                <span>Retour à l'accueil</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contenu des CGV */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="frelon-card border-0 frelon-depth-2">
            <div className="prose prose-lg max-w-none">
              
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-2xl mb-8 border border-orange-100">
                <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-6 w-6 text-orange-600 mr-2" />
                  Préambule
                </h2>
                <p className="text-gray-700">
                  Les présentes Conditions Générales de Vente (CGV) s'appliquent à tous les services 
                  de désinsectisation proposés par <strong className="frelon33-brand">Frelon33™</strong>.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                1. Champ d'application
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  Les présentes CGV régissent exclusivement les relations contractuelles entre 
                  <strong className="frelon33-brand"> Frelon33™</strong> et ses clients pour la 
                  prestation de services de désinsectisation.
                </p>
                <p>
                  Toute commande implique l'acceptation sans réserve des présentes CGV.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                2. Services proposés
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="frelon-card bg-green-50 border-green-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Services principaux
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Destruction de nids de frelons asiatiques</li>
                    <li>Destruction de nids de frelons européens</li>
                    <li>Élimination de nids de guêpes</li>
                    <li>Désinsectisation générale</li>
                  </ul>
                </div>
                
                <div className="frelon-card bg-blue-50 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Services complémentaires
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Diagnostic et expertise</li>
                    <li>Conseils préventifs</li>
                    <li>Suivi post-intervention</li>
                    <li>Intervention d'urgence</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                3. Devis et commandes
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="frelon-card bg-yellow-50 border-yellow-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <Calendar className="h-5 w-5 text-yellow-600 mr-2" />
                    Processus de commande
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Demande de devis (gratuite et sans engagement)</li>
                    <li>Visite de diagnostic sur site</li>
                    <li>Remise du devis détaillé</li>
                    <li>Acceptation du devis par le client</li>
                    <li>Planification de l'intervention</li>
                  </ol>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>Validité du devis :</strong> 30 jours à compter de sa date d'émission.
                  </p>
                  <p>
                    <strong>Acceptation :</strong> L'acceptation du devis par le client vaut commande ferme.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                4. Prix et tarification
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  Les prix sont indiqués en euros, toutes taxes comprises (TTC). 
                  En tant qu'auto-entrepreneur, <strong className="frelon33-brand">Frelon33™</strong> 
                  bénéficie de la franchise de TVA.
                </p>
                
                <div className="frelon-card bg-green-50 border-green-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Tarification
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Déplacement :</strong> Gratuit dans un rayon de 50 km</li>
                    <li><strong>Diagnostic :</strong> Gratuit et sans engagement</li>
                    <li><strong>Interventions :</strong> Selon devis personnalisé</li>
                    <li><strong>Urgences :</strong> Tarif majoré de 30% les weekends et jours fériés</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                5. Modalités de paiement
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="frelon-card bg-blue-50 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
                    Moyens de paiement
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Espèces</li>
                    <li>Chèque</li>
                    <li>Virement bancaire</li>
                    <li>Carte bancaire (sur site)</li>
                  </ul>
                </div>
                
                <div className="frelon-card bg-purple-50 border-purple-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Conditions de paiement
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Paiement comptant à la fin de l'intervention</li>
                    <li>Acompte de 30% pour interventions &gt; 500€</li>
                    <li>Délai de paiement : immédiat</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                6. Exécution des prestations
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <h3 className="text-xl font-semibold text-gray-900">Délais d'intervention</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Intervention standard :</strong> Sous 48h ouvrées</li>
                  <li><strong>Intervention urgente :</strong> Sous 24h (supplément applicable)</li>
                  <li><strong>Cas d'urgence absolue :</strong> Intervention le jour même si possible</li>
                </ul>
              </div>

              <div className="frelon-card bg-red-50 border-red-200 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                  Conditions préalables à l'intervention
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Accès libre et sécurisé au site d'intervention</li>
                  <li>Présence obligatoire du client ou de son représentant</li>
                  <li>Éloignement des personnes et animaux de la zone de traitement</li>
                  <li>Mise à disposition d'un point d'eau si nécessaire</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                7. Garanties
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="frelon-card bg-green-50 border-green-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Garantie de résultat
                  </h3>
                  <p className="text-gray-700">
                    <strong className="frelon33-brand">Frelon33™</strong> garantit l'efficacité de ses interventions. 
                    En cas de récidive dans les 30 jours suivant l'intervention, une nouvelle intervention 
                    sera effectuée gratuitement.
                  </p>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <h3 className="text-xl font-semibold text-gray-900">Exclusions de garantie</h3>
                  <p>La garantie ne s'applique pas :</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>En cas de nouvelle infestation (différente de l'intervention initiale)</li>
                    <li>Si les recommandations préventives n'ont pas été suivies</li>
                    <li>En cas de modification des conditions du site par le client</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                8. Responsabilité et assurance
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  <strong className="frelon33-brand">Frelon33™</strong> est couvert par une assurance 
                  responsabilité civile professionnelle pour tous ses services.
                </p>
                
                <div className="frelon-card bg-blue-50 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Limitation de responsabilité
                  </h3>
                  <p className="text-gray-700">
                    La responsabilité de <strong className="frelon33-brand">Frelon33™</strong> est limitée 
                    au montant de la prestation facturée. Sont exclus les dommages indirects et les pertes d'exploitation.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                9. Annulation et report
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <h3 className="text-xl font-semibold text-gray-900">Annulation par le client</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Plus de 24h avant :</strong> Annulation gratuite</li>
                  <li><strong>Moins de 24h avant :</strong> Frais d'annulation de 50€</li>
                  <li><strong>Le jour même :</strong> Facturation intégrale</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mt-6">Report d'intervention</h3>
                <p>
                  Un report d'intervention est possible sans frais supplémentaires si demandé 
                  au moins 24h à l'avance.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                10. Droit de rétractation
              </h2>
              
              <div className="frelon-card bg-yellow-50 border-yellow-200 mb-8">
                <p className="text-gray-700">
                  Conformément à la réglementation, le droit de rétractation ne s'applique pas aux prestations 
                  de services dont l'exécution a commencé avant la fin du délai de rétractation, 
                  avec l'accord express du consommateur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                11. Règlement des litiges
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  En cas de litige, une solution amiable sera recherchée avant tout recours contentieux.
                </p>
                
                <div className="frelon-card bg-purple-50 border-purple-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Médiation de la consommation
                  </h3>
                  <p className="text-gray-700">
                    Conformément à la réglementation, le client peut recourir gratuitement au service 
                    de médiation de la consommation dont relève <strong className="frelon33-brand">Frelon33™</strong>.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                12. Contact et réclamations
              </h2>
              
              <div className="frelon-card bg-green-50 border-green-200 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Service client
                </h3>
                <p className="text-gray-700">
                  Pour toute question ou réclamation concernant nos services :
                </p>
                <ul className="list-none space-y-2 mt-3 text-gray-700">
                  <li><strong>E-mail :</strong> frelon33000@gmail.com</li>
                  <li><strong>Téléphone :</strong> 06 76 58 30 38</li>
                  <li><strong>Délai de réponse :</strong> 48h maximum</li>
                </ul>
              </div>

              <div className="mt-12 text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
                </p>
                <button
                  onClick={scrollToTop}
                  className="frelon-btn frelon-btn-primary inline-flex items-center space-x-2 px-6 py-3"
                >
                  <ArrowUp className="h-5 w-5" />
                  <span>Retour en haut</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CGV;